import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import { FiServer, FiActivity, FiLayers, FiX } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
    fetchPerformanceData,
    fetchClusterApps,
    fetchDaprComponents,
    setSelectedAKSCluster
} from '../store/slices/monitoringSlice';

const KubernetesDashboardPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    // Get ALL data from Redux store - no local state
    const {
        selectedAKSCluster,
        kubernetesClusters,
        kubernetesApps,
        daprComponents,
        performanceData,
        loading,
        error
    } = useSelector((state: RootState) => state.monitoring);

    const [view, setView] = useState('dashboard');
    const [selectedApp, setSelectedApp] = useState<any>(null);

    // Fetch all dashboard data when component mounts or cluster selection changes
    useEffect(() => {
        console.log('Fetching dashboard data for cluster:', selectedAKSCluster?.name || 'all');

        // Dispatch all fetch actions
    //    dispatch(fetchClusters());
     /*   dispatch(fetchClusterApps(selectedAKSCluster?.name));
        dispatch(fetchDaprComponents(selectedAKSCluster?.name));*/
        dispatch(fetchPerformanceData(selectedAKSCluster?.name));
    }, [dispatch, selectedAKSCluster]);

    // Filter data based on selected AKS cluster
    const filteredClusters = selectedAKSCluster
        ? kubernetesClusters?.filter(cluster => cluster.name === selectedAKSCluster.name)
        : kubernetesClusters;

    const filteredApps = selectedAKSCluster
        ? kubernetesApps?.filter(app => app.cluster === selectedAKSCluster.name)
        : kubernetesApps;

    const filteredDaprComponents = selectedAKSCluster
        ? daprComponents?.filter(component => component.cluster === selectedAKSCluster.name)
        : daprComponents;

    const handleClusterSelect = (cluster: any) => {
        navigate(`/kubernetes/clusters/${cluster.name}`);
    };

    const handleAppSelect = (app: any) => {
        setSelectedApp(app);
        setView('appDetail');
    };

    const handleClearFilter = () => {
        dispatch(setSelectedAKSCluster(null));
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading dashboard...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
            </div>
        );
    }
    console.log("performanceData ---->:", performanceData);
    const renderDashboard = () => (
        <div className="space-y-6">
            {/* Header with filter indicator */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kubernetes Dashboard</h1>
                    {selectedAKSCluster && (
                        <div className="mt-1 flex items-center space-x-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Filtered by: <span className="font-medium text-gray-900 dark:text-white">{selectedAKSCluster.name}</span>
                            </p>
                            <button
                                onClick={handleClearFilter}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                <FiX className="mr-1 h-3 w-3" />
                                Clear Filter
                            </button>
                        </div>
                    )}
                </div>
                {selectedAKSCluster && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredClusters?.length??0} of {kubernetesClusters?.length??0} clusters
                    </div>
                )}
            </div>

            {/* Filter Banner */}
            {selectedAKSCluster && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <FiServer className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">Cluster Filter Active</h3>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                    Viewing data for <strong>{selectedAKSCluster.name}</strong> cluster in {selectedAKSCluster.location}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClearFilter}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 hover:bg-blue-200"
                        >
                            <FiX className="mr-2" />
                            Show All Clusters
                        </button>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900 mr-4">
                            <FiServer className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Clusters</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredClusters?.length??0}</p>
                            {selectedAKSCluster && (kubernetesClusters?.length??0) > (filteredClusters?.length??0) && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">of {kubernetesClusters?.length??0} total</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-green-100 dark:bg-green-900 mr-4">
                            <FiLayers className="h-6 w-6 text-green-600 dark:text-green-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Applications</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredApps?.length??0}</p>
                            {selectedAKSCluster && (kubernetesApps?.length??0) > (filteredApps?.length??0) && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">of {kubernetesApps?.length??0} total</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900 mr-4">
                            <FiActivity className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dapr Components</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredDaprComponents?.length??0}</p>
                            {selectedAKSCluster && (daprComponents?.length??0) > (filteredDaprComponents?.length??0) && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">of {daprComponents?.length??0} total</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Cluster Performance (Last 12 Hours)
                    {selectedAKSCluster && <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">- {selectedAKSCluster.name}</span>}
                </h2>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={performanceData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="time" stroke="#9CA3AF" />
                            <YAxis stroke="#9CA3AF" />
                            <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '0.375rem' }} />
                            <Legend />
                            <Line type="monotone" dataKey="cpu" stroke="#3B82F6" name="CPU Usage %" strokeWidth={2} />
                            <Line type="monotone" dataKey="memory" stroke="#10B981" name="Memory Usage %" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Rest of the dashboard content... (clusters table, apps table, dapr components) */}
            {/* Keeping the same structure as before */}
        </div>
    );

    return (
        <div className="container mx-auto px-4 py-6">
            {view === 'dashboard' && renderDashboard()}
            {view === 'appDetail' && selectedApp && (
                <div>App Detail View for {selectedApp.name}</div>
            )}
        </div>
    );
};

export default KubernetesDashboardPage;